import type { ComboboxProps } from "@v1/components/designSystem/combobox/Combobox";
import type { ComboboxOption } from "@v1/components/designSystem/combobox/ComboboxContext";
import type { DateAfter, DateRange } from "react-day-picker";
import type {
  ComparisonTimeFrame,
  RelativeTimeFrame,
  RelativeTimeFrameComboboxOption,
  SelectedTimePeriod,
} from "schemas/utils/analytics";

import * as React from "react";

import { TZDate } from "@date-fns/tz";
import twMerge from "@replo/design-system/utils/twMerge";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { DatePickerWithRange } from "@v1/components/designSystem/shadcn/core/date-range-picker";
import {
  calculateCompareRangePredefined,
  calculateCustomRange,
  calculateQueryRangesForRelativeTimeFrame,
  calculateRangesForCustomTimeFrame,
  MAIN_RELATIVE_TIME_FRAMES,
} from "analytics-utils/analytics";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { CalendarDays, Clock } from "lucide-react";
import { formatTimezoneCompact } from "replo-utils/lib/timezone";
import { DEFAULT_COMPARE_AT_RANGE_ID } from "schemas/analyticsRead";

import { useAnalyticsQueryContext } from "../contexts/AnalyticsQueryContext";
import useCurrentAnalyticsTimezone from "../useCurrentAnalyticsTimezone";

type AnalyticsTimeRangeComboboxProps<
  T extends RelativeTimeFrame | CompareRangeHandleChange,
> = Pick<ComboboxProps, "size"> & {
  handleRangeChange: (timeFrame: T) => void;
  isCompare: boolean;
  placeholderPrefix?: string;
  options: (ComboboxOption & { displayValue: string })[];
  selectedTimePeriod: SelectedTimePeriod;
  selectedComparePeriod: SelectedTimePeriod;
  timeZone: string;
};

type CompareRangeHandleChange = ComparisonTimeFrame | DateRange;
type MainRangeHandleChange = RelativeTimeFrame | DateRange;

const rangeCustomDatePicker = (
  dateRange: DateRange,
  handleRangeChange: (dateRange: DateRange) => void,
  setIsRangePickerOpen: (isOpen: boolean) => void,
  timeZone: string,
  size?: ComboboxProps["size"],
): React.ReactNode => {
  return (
    <DatePickerWithRange
      initialDateRange={dateRange}
      disabledDates={{ after: new TZDate(new Date(), timeZone) } as DateAfter}
      updateAction={(dateRange) => {
        handleRangeChange({
          from: dateRange.from
            ? fromZonedTime(dateRange.from, timeZone)
            : undefined,
          to: dateRange.to ? fromZonedTime(dateRange.to, timeZone) : undefined,
        });
        setIsRangePickerOpen(false);
      }}
      popoverTriggerComponent={() => (
        <span
          className={twMerge(
            "text-primary flex cursor-pointer items-center font-medium",
            size === "sm" ? "text-xs" : "text-sm",
          )}
        >
          <CalendarDays size={size === "sm" ? 12 : 14} className="mr-1.5" />
          Custom date range
        </span>
      )}
      closeAction={() => setIsRangePickerOpen(false)}
      rangeExtendsOnSelect={false}
    />
  );
};

export const AnalyticsTimeRangeCombobox = <
  T extends MainRangeHandleChange | CompareRangeHandleChange,
>({
  handleRangeChange,
  isCompare,
  selectedTimePeriod,
  selectedComparePeriod,
  timeZone,
  size = "sm",
  ...props
}: AnalyticsTimeRangeComboboxProps<T>) => {
  let selectedValue;
  const { updatedRanges, selectedTimeFrame } =
    selectedTimePeriod.type === "custom"
      ? calculateRangesForCustomTimeFrame({
          start: selectedTimePeriod.value.from,
          end: selectedTimePeriod.value.to,
          timeZone,
        })
      : calculateQueryRangesForRelativeTimeFrame({
          timeFrame: selectedTimePeriod.value as RelativeTimeFrame,
          timeZone,
        });

  let dateRange: DateRange;

  if (!isCompare) {
    selectedValue = selectedTimeFrame;
    dateRange = {
      from: toZonedTime(updatedRanges.mainRange.startDatetime, timeZone),
      to: toZonedTime(updatedRanges.mainRange.endDatetime, timeZone),
    };
  } else {
    const { range: compareAtRange, selectedPeriod } =
      selectedComparePeriod.type === "custom"
        ? calculateCustomRange({
            start: selectedComparePeriod.value.from,
            end: selectedComparePeriod.value.to,
            timeZone,
            rangeId: DEFAULT_COMPARE_AT_RANGE_ID,
          })
        : calculateCompareRangePredefined(
            selectedComparePeriod.value as ComparisonTimeFrame,
            updatedRanges.mainRange,
          );

    selectedValue = selectedPeriod;
    dateRange = {
      from: toZonedTime(compareAtRange.startDatetime, timeZone),
      to: toZonedTime(compareAtRange.endDatetime, timeZone),
    };
  }

  const [isRangePickerOpen, setIsRangePickerOpen] = React.useState(false);

  const footerContent = (
    <>
      {rangeCustomDatePicker(
        dateRange,
        (dateRange) => handleRangeChange(dateRange as T),
        setIsRangePickerOpen,
        timeZone,
        size,
      )}
    </>
  );

  const selectedOption = props.options.find(
    (option) => option.value === selectedValue,
  );

  const timezoneTitle = (
    <div className="text-muted border-border border-b px-2 pb-2 text-center text-xs">
      <div className="flex items-center justify-center gap-1">
        <Clock size={14} className="text-muted" />
        <span className="italic">{formatTimezoneCompact(timeZone)}</span>
      </div>
    </div>
  );

  return (
    <Combobox
      {...props}
      value={selectedValue}
      size={size}
      onChange={(value) => {
        handleRangeChange(value as T);
      }}
      open={isRangePickerOpen}
      onOpenChange={(isOpen) => {
        setIsRangePickerOpen(isOpen);
      }}
      title={timezoneTitle}
      trigger={
        <Combobox.SelectionButton
          title={
            selectedOption?.displayValue ??
            `${props.placeholderPrefix ? `${props.placeholderPrefix} ` : ""}${selectedValue}`
          }
          startEnhancer={<CalendarDays className="h-4 w-4" />}
        />
      }
      footer={
        footerContent && (
          <Combobox.Footer size="sm">{footerContent}</Combobox.Footer>
        )
      }
    />
  );
};

export const AnalyticsMainRangeCombobox: React.FC<{
  options?: RelativeTimeFrameComboboxOption[];
}> = ({ options = MAIN_RELATIVE_TIME_FRAMES }) => {
  const { query, dispatchAnalyticsQuery } = useAnalyticsQueryContext();
  const timeZone = useCurrentAnalyticsTimezone();

  const handleMainRangeChange = (
    selectedTimeFrameValue: RelativeTimeFrame | DateRange,
  ) => {
    dispatchAnalyticsQuery({
      type: "updateMainRange",
      payload: selectedTimeFrameValue,
    });
  };

  return (
    <AnalyticsTimeRangeCombobox
      options={options}
      handleRangeChange={handleMainRangeChange}
      isCompare={false}
      selectedTimePeriod={query.selectedTimePeriod}
      selectedComparePeriod={query.selectedComparePeriod}
      timeZone={timeZone}
    />
  );
};
